import { graphql } from "@/config/graphql";
import { dateWithoutTimezone } from "@/utils/date";
import { ApolloError, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  alpha,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { addDays, addMinutes, differenceInDays, format } from "date-fns";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

interface Props {
  onClose: () => void;
}
const getLastReportsQuery = graphql(`
  query getLastReports {
    currentUser {
      selectedPlant {
        plant {
          lastReports(params: { days: 14 }) {
            id
            status
            date
          }
        }
      }
    }
  }
`);
const createReportMutation = graphql(`
  mutation createReport($params: CreateReportParams!) {
    createReport(params: $params) {
      id
    }
  }
`);

export const NewReportDialog = (props: Props) => {
  const theme = useTheme();
  const [date, setDate] = useState<null | Date>(new Date());
  const [error, setError] = useState<null | string>(null);
  const navigate = useNavigate();
  const { data, refetch, loading: loadingData } = useQuery(getLastReportsQuery);
  const [createReport, { loading }] = useMutation(createReportMutation);

  const submittedReports = new Set(
    data?.currentUser?.selectedPlant?.plant?.lastReports
      ?.filter((report) => report?.status === "SUBMITTED")
      .map((report) => report?.date ?? "")
      .filter(Boolean) || []
  );
  const draftReports = new Set(
    data?.currentUser?.selectedPlant?.plant?.lastReports
      ?.filter((report) => report?.status === "DRAFT")
      .map((report) => report?.date ?? "")
      .filter(Boolean) || []
  );

  const isReportSubmittedOnDate = useCallback(
    (day: Date) => {
      return !!data?.currentUser?.selectedPlant?.plant?.lastReports?.some(
        (report) => {
          return (
            report &&
            report.status === "SUBMITTED" &&
            addMinutes(
              new Date(report.date),
              new Date().getTimezoneOffset()
            ).toDateString() === day.toDateString()
          );
        }
      );
    },
    [data]
  );
  const shouldDisableDate = useCallback(
    (day: Date) => {
      const today = new Date();
      if (day > today) return true;
      if (differenceInDays(today, day) > 4) return true;
      return isReportSubmittedOnDate(day);
    },
    [isReportSubmittedOnDate]
  );
  const submit = async () => {
    if (!date) return;
    if (loading) return;
    if (shouldDisableDate(date)) {
      setError("No puedes emitir reporte para esta fecha");
      return;
    }
    try {
      const res = await createReport({
        variables: {
          params: {
            date: date.toISOString().replace(/T.*/, ""),
          },
        },
      });
      if (res.data?.createReport?.id) {
        navigate(`/formulario-reporte/${res.data.createReport.id}/produccion`);
      }
      props.onClose();
    } catch (e) {
      if (e instanceof ApolloError) {
        if (e.message === "ALREADY_SUBMITTED_REPORT") {
          toast.error("Ya existe un reporte para esta fecha");
          refetch();
          return;
        }
      }
      console.error(e);
      toast.error("Ocurrió un error al crear el reporte. Intente de nuevo");
    }
  };

  return (
    <Dialog open onClose={props.onClose}>
      <DialogTitle>Nuevo reporte</DialogTitle>
      <DialogContent>
        <DatePicker
          sx={{
            ".MuiDialogActions-root": { display: "none" },
          }}
          slotProps={{
            day: (props) => {
              const dayWithInfo =
                Math.abs(differenceInDays(props.day, new Date())) < 14 &&
                props.day < new Date();
              const currentDate = format(
                dateWithoutTimezone(props.day),
                "yyyy-MM-dd"
              );
              const submittedReport = submittedReports.has(currentDate);
              const draftReport = draftReports.has(currentDate);
              const color = dayWithInfo
                ? submittedReport
                  ? "green"
                  : draftReport
                  ? theme.palette.primary.main
                  : theme.palette.error.main
                : "rgba(0,0,0,0.38)";
              return {
                sx: {
                  color,
                  transition: "0.5s",
                  "&.Mui-selected": {
                    color,
                    fontWeight: "bold",
                    fontSize: "14px",
                    background: (theme) =>
                      alpha(theme.palette.primary.main, 0.2),
                    "&:hover, &:focus": {
                      fontWeight: "bold",
                      background: (theme) =>
                        alpha(theme.palette.primary.main, 0.2),
                    },
                  },
                  "&.Mui-disabled, &.Mui-disabled:not(.Mui-selected)": {
                    textDecoration: "line-through",
                    color,
                  },
                },
              };
            },
          }}
          minDate={addDays(new Date(), -2)}
          maxDate={new Date()}
          shouldDisableDate={shouldDisableDate}
          showDaysOutsideCurrentMonth
          value={date}
          onChange={(newDate) => {
            setDate(newDate);
            setError(null);
          }}
          disabled={loadingData}
        />
        <FormHelperText error>{error}</FormHelperText>
      </DialogContent>
      <DialogActions>
        <Button onClick={submit} variant="contained">
          {loading ? (
            <>
              Creando{" "}
              <CircularProgress
                size={20}
                sx={{ color: "white", marginLeft: 1 }}
              />
            </>
          ) : (
            "Confirmar"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
